import { fixtureMatchHidden } from '../config/featureHides';
import { TOOLS, visibleTools } from './toolsCatalog';

/**
 * Prototype v6 navigation, as pure data + functions (no React): the sidebar generated from the package and the
 * hubs (tab bars Layout draws above EXISTING pages). Every tab points at an existing URL; no URL changes.
 *
 * ctx = { has(key), features }  — `has` is useEntitlements().has: only an explicit `false` hides, a missing
 * key is shown (the 6A rule, unchanged). `features` is package.features (only used where a ROUTE exists
 * only for a literal true, i.e. عروضي).
 */

/* ── predicates ───────────────────────────────────────────────────────────────────── */
// THE RULE: an entry/tab is shown only if the page it opens renders real, package-included content, so each
// predicate equals its page's EFFECTIVE gate, with the page's own strictness:
//   strict(k)  = the page sits behind FeatureGate (needs an explicit true; a missing key locks it)
//   ctx.has(k) = the page sits behind EntitlementGuard or has no gate (only an explicit false hides)
const strict = (k) => ({ features }) => features?.[k] === true;
// RemoteControl.jsx: `allowed = remote_control === true` AND the page shows its lock card when hardware_control
// and birthday_mode are BOTH explicitly off (allGated, loose `has`).
export const remotePageOpen = ({ has, features }) => features?.remote_control === true && (has('hardware_control') || has('birthday_mode'));
// «الجدول» sub-pages. The home page (/schedule) is only tiles linking to these two, so it is worth a tab only
// when there are at least two to choose from.
export const themesOpen = ({ has }) => has('schedule') && has('theme_schedule');
export const wallsOpen = ({ has }) => has('schedule') && has('video_wall');
export const scheduleTileCount = (ctx) => [themesOpen, wallsOpen].filter((f) => f(ctx)).length;

/* ── hubs ─────────────────────────────────────────────────────────────────────────── */
// Each tab: id, labelKey (nav6.*), path (where the tab goes), prefixes (routes that belong to it),
// exact (match only the path itself), visible(ctx). A tab may have `chips` (second row, same page family).
export const HUBS = {
  moments: {
    labelKey: 'nav6.hub.moments',
    tabs: [
      { id: 'promos',   labelKey: 'nav6.tab.promos',    path: '/apps/promos',     prefixes: ['/apps/promos'],     visible: ({ features }) => features?.venue_overlay_promos === true },
      { id: 'happy',    labelKey: 'nav6.tab.happy',     path: '/apps/happy-hour', prefixes: ['/apps/happy-hour'], visible: strict('happy_hour') },
      { id: 'birthday', labelKey: 'nav6.tab.birthday',  path: '/apps/birthday',   prefixes: ['/apps/birthday'],   visible: strict('birthday_mode') },
      { id: 'spin',     labelKey: 'nav6.tab.spin',      path: '/apps/spin-win',   prefixes: ['/apps/spin-win'],   visible: strict('spin_and_win') },
    ],
  },
  guests: {
    labelKey: 'nav6.hub.guests',
    tabs: [
      { id: 'registry',    labelKey: 'nav6.tab.registry',    path: '/customers',          prefixes: ['/customers'],  visible: ({ has }) => has('crm_customers') },
      { id: 'loyalty',     labelKey: 'nav6.tab.loyalty',     path: '/loyalty/settings',   prefixes: ['/loyalty'],    visible: strict('qr_loyalty') },
      { id: 'suggestions', labelKey: 'nav6.tab.suggestions', path: '/automation',         prefixes: ['/automation'], visible: (ctx) => strict('crm_customers')(ctx) && ctx.has('suggestions') },
      { id: 'campaigns',   labelKey: 'nav6.tab.campaigns',   path: '/marketing/campaigns', prefixes: ['/marketing'], visible: strict('marketing_email') },
    ],
  },
  control: {
    labelKey: 'nav6.hub.control',
    tabs: [
      { id: 'play',   labelKey: 'nav6.tab.play',   path: '/remote-control',  prefixes: ['/remote-control'],  visible: remotePageOpen },
      { id: 'scenes', labelKey: 'nav6.tab.scenes', path: '/hardware/scenes', prefixes: ['/hardware/scenes'], visible: ({ has }) => has('hardware_control') },
    ],
  },
  schedule: {
    labelKey: 'nav6.hub.schedule',
    tabs: [
      { id: 'upcoming', labelKey: 'nav6.tab.upcoming', path: '/schedule',                  prefixes: ['/schedule'], exact: true, visible: (ctx) => scheduleTileCount(ctx) >= 2 },
      { id: 'themes',   labelKey: 'nav6.tab.themes',   path: '/schedule/themes',           prefixes: ['/schedule/themes'],        visible: themesOpen },
      { id: 'walls',    labelKey: 'nav6.tab.walls',    path: '/apps/video-wall/schedule',  prefixes: ['/apps/video-wall/schedule'], visible: wallsOpen },
    ],
  },
  account: {
    labelKey: 'nav6.hub.account',
    tabs: [
      { id: 'plan',     labelKey: 'nav6.tab.plan',     path: '/profile',           prefixes: ['/profile', '/money'], visible: () => true },
      { id: 'branches', labelKey: 'nav6.tab.branches', path: '/schedule/locations', prefixes: ['/schedule/locations'], visible: ({ has }) => has('locations') },
      {
        id: 'ads', labelKey: 'nav6.tab.ads', path: '/billing/ad-earnings',
        prefixes: ['/billing', '/apps/ad-marketplace', '/apps/smart-ad-scheduler'],
        visible: ({ has }) => has('ad_marketplace'),
        chips: [
          { id: 'earnings',    labelKey: 'nav6.chip.ad_earnings',    path: '/billing/ad-earnings',       prefixes: ['/billing/ad-earnings'],       visible: ({ has }) => has('ad_marketplace') },
          { id: 'settings',    labelKey: 'nav6.chip.ad_settings',    path: '/billing/ads-settings',      prefixes: ['/billing/ads-settings'],      visible: ({ has }) => has('ad_marketplace') },
          { id: 'marketplace', labelKey: 'nav6.chip.ad_marketplace', path: '/apps/ad-marketplace',       prefixes: ['/apps/ad-marketplace'],       visible: strict('ad_marketplace') },
          { id: 'smart',       labelKey: 'nav6.chip.ad_smart',       path: '/apps/smart-ad-scheduler',   prefixes: ['/apps/smart-ad-scheduler'],   visible: (c) => !fixtureMatchHidden() && strict('smart_ad_scheduler')(c) },
        ],
      },
    ],
  },
};

const underPrefix = (pathname, p) => pathname === p || pathname.startsWith(p + '/');
const tabOwns = (tab, pathname) => (tab.exact ? tab.prefixes.some((p) => pathname === p) : tab.prefixes.some((p) => underPrefix(pathname, p)));

/** Visible tabs of a hub for this package. */
export const visibleTabs = (hubId, ctx) => HUBS[hubId].tabs.filter((t) => t.visible(ctx));

/** Where a hub's sidebar item goes: its first visible tab's EXISTING url. null when no tab is visible. */
export const hubEntry = (hubId, ctx) => visibleTabs(hubId, ctx)[0]?.path ?? null;

/**
 * The hub a route belongs to (for the tab bar), and the active tab. Hub ownership does NOT depend on the
 * package: a deep link to a feature the package turns off still shows that hub's bar (without the missing tab)
 * above the page's own calm card. Returns null for routes outside every hub.
 */
export function hubForPath(pathname, ctx, locationState) {
  // /profile belongs to the account hub only for the subscription tab: Account / Emergency settings (reached
  // from the profile menu with state.activeTab) stay plain Profile pages.
  if (underPrefix(pathname, '/profile') && ['account', 'emergency'].includes(locationState?.activeTab)) return null;
  for (const [hubId, hub] of Object.entries(HUBS)) {
    const tab = hub.tabs.find((t) => tabOwns(t, pathname));
    if (tab) {
      const tabs = visibleTabs(hubId, ctx);
      return { hubId, hub, tab, tabs, chips: (tab.chips || []).filter((c) => c.visible(ctx)) };
    }
  }
  return null;
}

/** Sidebar highlighting for a hub: prefix matches, plus exact-only paths (the schedule hub's /schedule must not
 *  also light on /schedule/locations, which belongs to the account hub). */
const hubMatch = (hubId) => ({
  matchPrefixes: HUBS[hubId].tabs.filter((t) => !t.exact).flatMap((t) => t.prefixes),
  exactPaths: HUBS[hubId].tabs.filter((t) => t.exact).flatMap((t) => t.prefixes),
});

/* ── sidebar ──────────────────────────────────────────────────────────────────────── */
/**
 * Items in the shape MenuList already renders: { path, name, iconKey, visible, matchPrefixes, hero?, sectionLabel?, count? }.
 * `t` translates nav6.* keys. Order and grouping follow v6:
 *   الرئيسية · إنشاء · العرض · [اللحظات والضيوف] · التحكم والأدوات · الحساب
 */
export function buildSidebar(ctx, t) {
  const { has } = ctx;
  const items = [];
  // `end`: react-router's own auto-active (a path PREFIX match) must not light a second item (e.g. /apps for أدوات
  // الشاشة on /apps/happy-hour, or /schedule on /schedule/locations): our matchPrefixes decide, exactly one item wins.
  const add = (it, section) => items.push({ visible: true, end: true, ...it, ...(section ? { sectionLabel: section } : {}) });

  add({ path: '/dashboard', name: t('nav6.item.home'), iconKey: 'home', matchPrefixes: ['/dashboard'] });
  if (has('create')) add({ path: '/create', name: t('nav6.item.create'), iconKey: 'create', hero: true, matchPrefixes: ['/create'] });

  // العرض
  const show = [{ path: '/devices/list', name: t('nav6.item.screens'), iconKey: 'screens', matchPrefixes: ['/devices', '/screens'] }];
  if (has('content')) show.push({ path: '/content', name: t('nav6.item.content'), iconKey: 'content', matchPrefixes: ['/content', '/media', '/playlist', '/links'] });
  else if (has('music')) show.push({ path: '/content', name: t('nav6.item.music'), iconKey: 'content', matchPrefixes: ['/content', '/media', '/playlist', '/links'] });
  if (has('programs')) show.push({ path: '/campaigns/list', name: t('nav6.item.programs'), iconKey: 'shows', matchPrefixes: ['/campaigns', '/interactive'] });
  const scheduleEntry = hubEntry('schedule', ctx);
  if (scheduleEntry) show.push({ path: scheduleEntry, name: t('nav6.item.schedule'), iconKey: 'schedule', ...hubMatch('schedule') });

  const momentsEntry = hubEntry('moments', ctx);
  const guestsEntry = hubEntry('guests', ctx);
  const grow = [];
  if (momentsEntry) grow.push({ path: momentsEntry, name: t('nav6.item.moments'), iconKey: 'moments', ...hubMatch('moments') });
  if (guestsEntry) grow.push({ path: guestsEntry, name: t('nav6.item.guests'), iconKey: 'customers', ...hubMatch('guests') });

  show.forEach((it, i) => add(it, i === 0 ? t('nav6.group.show') : null));
  // The «اللحظات والضيوف» header appears only when BOTH hubs exist; a single hub sits under العرض with no header.
  grow.forEach((it, i) => add(it, grow.length > 1 && i === 0 ? t('nav6.group.moments_guests') : null));

  // التحكم والأدوات
  const ctrl = [];
  const controlEntry = hubEntry('control', ctx);
  if (controlEntry) ctrl.push({ path: controlEntry, name: t('nav6.item.control'), iconKey: 'remote', hero: true, ...hubMatch('control') });
  if (!fixtureMatchHidden() && ctx.features?.match_scheduler === true) ctrl.push({ path: '/apps/match-scheduler', name: t('nav6.item.matches'), iconKey: 'matches', matchPrefixes: ['/apps/match-scheduler'] });
  const tools = visibleTools(has, ctx.features);
  const toolIds = [...new Set(tools.map((x) => x.tool))];
  if (toolIds.length === 1) {
    // One-item rule: a group with a single tool shows that tool under its own name.
    ctrl.push({ path: tools[0].url, name: t(tools[0].tk), iconKey: 'apps', matchPrefixes: tools.map((x) => x.url) });
  } else if (toolIds.length > 1) {
    ctrl.push({
      path: '/apps', name: t('nav6.item.tools'), iconKey: 'apps', count: tools.length, // one per CARD on /apps (RSS and Custom RSS are two cards), from the same visibleTools list the page renders
      // explicit tool URLs (NOT the bare '/apps' prefix: moments, matches and the ad pages also live under /apps/*)
      matchPrefixes: [...new Set(TOOLS.map((x) => x.url))],
      exactPaths: ['/apps'],
    });
  }
  ctrl.forEach((it, i) => add(it, i === 0 ? t('nav6.group.control_tools') : null));

  // الحساب
  const accountEntry = hubEntry('account', ctx) || '/profile';
  const wide = has('locations') || has('ad_marketplace');
  add(
    { path: accountEntry, name: t(wide ? 'nav6.item.account_plan' : 'nav6.item.plan'), iconKey: 'money', ...hubMatch('account') },
    t('nav6.group.account'),
  );

  return items;
}
